/**
 * Queries Firestore do programa Creator (B3 Fase 4).
 *
 * Lê `affiliate_clicks/{uid}_{month}` (counter por mês) e
 * `payout_requests` (status pending/paid).
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as limitFn,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../lib/firebase/config";

const FUNCTIONS_REGION =
  (import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined) ?? "us-central1";

export interface AffiliateClicksMonth {
  month: string;
  totalClicks: number;
  networks: Partial<Record<"amazon" | "shopee" | "awin" | "magalu", number>>;
  /** Origem do clique — só existe em docs a partir de 20/07. */
  sources: Partial<Record<"list" | "referral", number>>;
}

/**
 * Tags PRÓPRIAS do creator no payload da callable.
 *
 * ⚠️ Uma rede só desde 05/08/2026 (eram 4) — Amazon, Shopee e Magalu saíram
 * do canal de tag própria. Ver `CREATOR_TAG_NETWORKS` em
 * `features/affiliates/lib/referralPrograms.ts`.
 *
 * Note que `AffiliateClicksMonth.networks` acima segue com as 4: ele lê docs
 * de telemetria, que podem ser antigos. Escrita encolheu, leitura é tolerante.
 */
export type CreatorTagMap = {
  awin: string | null;
};

export type RedeemCreatorCodeResult =
  | { kind: "activation"; applied: true; replay: boolean; proExpiresAt: number; referralCode: string }
  | { kind: "referral"; applied: true; replay: boolean; creatorId: string; referralExpiresAt: number }
  | { kind: "referral"; applied: false; reason: "already_referred" };

/**
 * Resgata um código do Livra Creators. O servidor decide se é código de
 * ativação (vira creator) ou de indicação (atribui referral) — o client
 * manda só o texto colado.
 */
export async function redeemCreatorCode(code: string): Promise<RedeemCreatorCodeResult> {
  const functions = getFunctions(getApp(), FUNCTIONS_REGION);
  const fn = httpsCallable<{ code: string }, RedeemCreatorCodeResult>(
    functions,
    "redeemCreatorCode",
  );
  const res = await fn({ code });
  return res.data;
}

/**
 * Grava as tags de afiliado do creator. Passa por callable porque
 * `creatorTags` virou client-locked em 20/07 — escrita direta no perfil
 * volta `permission-denied`.
 */
export async function setCreatorTags(tags: CreatorTagMap): Promise<CreatorTagMap> {
  const functions = getFunctions(getApp(), FUNCTIONS_REGION);
  const fn = httpsCallable<{ tags: CreatorTagMap }, { tags: CreatorTagMap }>(
    functions,
    "setCreatorTags",
  );
  const res = await fn({ tags });
  return res.data.tags;
}

/** Contagem de indicações do creator, lida do próprio doc de código. */
export async function getReferralSignupCount(referralCode: string): Promise<number> {
  try {
    const snap = await getDoc(doc(db, "creator_codes", referralCode));
    if (!snap.exists()) return 0;
    return (snap.data().currentUses as number | undefined) ?? 0;
  } catch (err) {
    // Reportado, não engolido: "0 indicações" e "falhou ao ler" são
    // indistinguíveis na tela, que é como o BUG-41 passou despercebido.
    console.warn("[Lura] falha ao ler contador de indicações", err);
    return 0;
  }
}

export interface PayoutRequestSummary {
  requestId: string;
  amountBRL: number;
  month: string;
  status: "pending" | "paid" | "rejected";
  createdAtMs: number;
  paidAtMs?: number;
}

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export async function getCurrentMonthClicks(uid: string): Promise<AffiliateClicksMonth> {
  const month = currentMonthKey();
  const ref = doc(db, "affiliate_clicks", `${uid}_${month}`);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    return { month, totalClicks: 0, networks: {}, sources: {} };
  }
  const data = snap.data() as {
    totalClicks?: number;
    networks?: AffiliateClicksMonth["networks"];
    sources?: AffiliateClicksMonth["sources"];
  };
  return {
    month,
    totalClicks: typeof data.totalClicks === "number" ? data.totalClicks : 0,
    networks: data.networks ?? {},
    sources: data.sources ?? {},
  };
}

export async function listMyPayouts(uid: string, limit = 5): Promise<PayoutRequestSummary[]> {
  const q = query(
    collection(db, "payout_requests"),
    where("creatorUid", "==", uid),
    orderBy("createdAt", "desc"),
    limitFn(limit),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data() as {
      amountBRL?: number;
      month?: string;
      status?: PayoutRequestSummary["status"];
      createdAt?: { toMillis?: () => number };
      paidAt?: { toMillis?: () => number };
    };
    return {
      requestId: d.id,
      amountBRL: typeof data.amountBRL === "number" ? data.amountBRL : 0,
      month: data.month ?? "",
      status: data.status ?? "pending",
      createdAtMs: data.createdAt?.toMillis?.() ?? 0,
      paidAtMs: data.paidAt?.toMillis?.(),
    };
  });
}

export async function hasPendingPayout(uid: string): Promise<boolean> {
  const q = query(
    collection(db, "payout_requests"),
    where("creatorUid", "==", uid),
    where("status", "==", "pending"),
    limitFn(1),
  );
  const snap = await getDocs(q);
  return !snap.empty;
}

export async function requestPayout(): Promise<{ requestId: string; amountBRL: number }> {
  const functions = getFunctions(getApp(), FUNCTIONS_REGION);
  const fn = httpsCallable<unknown, { requestId: string; amountBRL: number; status: string }>(
    functions,
    "requestCreatorPayout",
  );
  const result = await fn({});
  return { requestId: result.data.requestId, amountBRL: result.data.amountBRL };
}

/**
 * Handle público de um creator, para dizer ao leitor QUEM o indicou.
 *
 * `redeemCreatorCode` devolve `creatorId` (uid) — mostrar o uid cru na UI
 * não diz nada a ninguém. Cache module-level porque a tela de Settings
 * remonta a cada troca de aba e o valor não muda dentro da sessão.
 */
const handleCache = new Map<string, Promise<string | null>>();

export function fetchCreatorHandle(uid: string): Promise<string | null> {
  if (!uid) return Promise.resolve(null);
  const cached = handleCache.get(uid);
  if (cached) return cached;

  const promise = getDoc(doc(db, "users", uid))
    .then((snap) => {
      if (!snap.exists()) return null;
      const data = snap.data() as { username?: string; displayName?: string };
      return data.username ?? data.displayName ?? null;
    })
    .catch((err) => {
      // Degrada pra mensagem genérica ("um creator"), nunca quebra o resgate:
      // a atribuição já foi feita no servidor a esta altura.
      console.warn("[Livra] falha ao ler handle do creator", err);
      handleCache.delete(uid);
      return null;
    });

  handleCache.set(uid, promise);
  return promise;
}
